import React from 'react';

/**
 * ErrorBoundary — 2026-08-21.
 *
 * Until now the app had NO error boundary anywhere (grepped: zero hits for
 * componentDidCatch / getDerivedStateFromError). React 18 unmounts the whole
 * tree when a render throws, so any single component bug blanked the entire
 * page — nav included — with no message. Fredrik hit exactly that on "Back to
 * Dashboard" during the 2026-08-21 prod E2E: white page, nothing in the
 * backend log, recovered only by a manual reload.
 *
 * Wrapping each module panel means a crash costs that panel, not the session:
 * the header and tabs keep working, so the user can switch away and back.
 *
 * `resetKey` clears a caught error when it changes — App passes the active
 * tab, so navigating away from a broken panel and back retries the render
 * instead of showing the fallback forever.
 */

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Shown in the fallback, e.g. "Content Review". */
  moduleName?: string;
  /** Changing this value clears a caught error and re-renders children. */
  resetKey?: string | number;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, componentStack: null });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the console trace — it is what a bug report needs, and the
    // fallback below shows only the message.
    console.error(`[ErrorBoundary${this.props.moduleName ? ': ' + this.props.moduleName : ''}]`, error, info.componentStack);
    this.setState({ componentStack: info.componentStack || null });
  }

  handleRetry = () => this.setState({ error: null, componentStack: null });

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    const where = this.props.moduleName ? ` in ${this.props.moduleName}` : '';
    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          padding: 20, background: '#fff', borderRadius: 12,
          border: '1px solid #f5c2c7', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <h3 style={{ margin: '0 0 8px', color: '#842029', fontSize: 16 }}>
          Something went wrong{where}
        </h3>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#555', lineHeight: 1.5 }}>
          This panel stopped rendering. Nothing you were viewing was changed or lost —
          the rest of CellcoHub still works, and you can switch tabs. If it keeps
          happening, send this message along with the console output (F12 &rarr; Console).
        </p>
        <pre style={{
          margin: '0 0 12px', padding: 12, background: '#f8f9fa', borderRadius: 6,
          fontSize: 12, color: '#842029', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          maxHeight: 160, overflowY: 'auto'
        }}>
          {error.message || String(error)}
          {componentStack ? `\n${componentStack.split('\n').slice(0, 4).join('\n')}` : ''}
        </pre>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="review-btn review-btn-primary"
            onClick={this.handleRetry}
            style={{ fontSize: 13 }}
          >
            Try again
          </button>
          <button
            className="review-btn review-btn-outline"
            onClick={() => window.location.reload()}
            style={{ fontSize: 13 }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
